import type { DraftRuleScenario } from '../lib/api.ts'
import { Input } from './Input.tsx'
import { Checkbox } from './Checkbox.tsx'

/**
 * The facts the operational rules read, set by hand for a simulation.
 *
 * A simulation derives its quotation from an uploaded prescription, and none of
 * this is on the page: whether the substance is controlled, when the
 * prescription was written, which branch will compound it, what the customer
 * asked for. So every rule that reads them was unreachable in a simulation --
 * fifteen rules with no way to watch one fire short of waiting for a real
 * requisition to happen to break.
 *
 * Everything is optional, and empty means unknown rather than false. That is
 * the same promise the rules make of a real ERP: a field nobody recorded is
 * not a field that says no, so a scenario left blank produces exactly the
 * simulation this screen produced before the section existed.
 */
export function RuleScenario({
  scenario,
  onChange,
}: {
  scenario: DraftRuleScenario
  onChange: (patch: DraftRuleScenario) => void
}) {
  const num = (value: string) => (value.trim() === '' ? null : Number(value))

  return (
    <article className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-tPSemi text-text-strong">Cenário das regras</h3>
        <p className="text-tSmall text-text-weak">
          O que o ERP diria em volta deste orçamento. Nada aqui sai da receita — deixe em branco
          para simular um ERP que não informa o campo.
        </p>
      </div>

      <Input
        label="Data da receita"
        type="date"
        value={scenario.prescribedOn ?? ''}
        onChange={(e) => onChange({ prescribedOn: e.target.value === '' ? null : e.target.value })}
      />

      <Input
        label="Dias de tratamento"
        type="number"
        value={scenario.treatmentDays == null ? '' : String(scenario.treatmentDays)}
        onChange={(e) => onChange({ treatmentDays: num(e.target.value) })}
      />

      <Input
        label="Filial que manipula"
        value={scenario.compoundingBranchCode ?? ''}
        onChange={(e) =>
          onChange({ compoundingBranchCode: e.target.value === '' ? null : e.target.value })
        }
      />

      {/* Free text, because that is how the customer's record holds it and how
          the rule reads it: "incolor", "sem lactose", "cápsula vegetal". */}
      <Input
        label="Observações do cliente"
        value={scenario.patientNotes ?? ''}
        onChange={(e) => onChange({ patientNotes: e.target.value === '' ? null : e.target.value })}
      />

      <Input
        label="Tipo de cápsula (V = vegetal, T = entérica)"
        value={scenario.capsuleKind ?? ''}
        onChange={(e) => onChange({ capsuleKind: e.target.value === '' ? null : e.target.value })}
      />

      <Input
        label="Peso do animal (kg)"
        type="number"
        value={scenario.animalWeightKg == null ? '' : String(scenario.animalWeightKg)}
        onChange={(e) => onChange({ animalWeightKg: num(e.target.value) })}
      />

      <Checkbox
        label="Receita veterinária"
        checked={scenario.veterinary === true}
        onChange={(e) => onChange({ veterinary: e.target.checked ? true : null })}
      />

      {/*
        Not a physician, rather than is one.
        
        The posology rule fires when somebody who is not a doctor wrote
        "conforme orientação médica", and unknown must never read as "not a
        doctor" -- most prescriptions are written by one, so the default would
        flag the ordinary case.
      */}
      <Checkbox
        label="Prescritor não é médico"
        checked={scenario.physician === false}
        onChange={(e) => onChange({ physician: e.target.checked ? false : null })}
      />

      <Checkbox
        label="Liberação modificada"
        checked={scenario.modifiedRelease === true}
        onChange={(e) => onChange({ modifiedRelease: e.target.checked ? true : null })}
      />
    </article>
  )
}
